import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchUserModules,
  fetchCurrentSubscription,
  fetchTrialModules,
  fetchSubscriptionPlans,
  type NormalizedUserModule,
} from "@repo/api-client";
import type { NavigationItem, NavigationSection } from "@repo/types";
import { cn } from "@repo/ui-web";
import { resolveIconComponentName } from "@/config/icon-registry";
import {
  buildEnabledModuleCodes,
  buildModuleLookup,
  extractPlanModuleCodes,
  isSubscriptionCurrentlyActive,
  isSubscriptionExpiredOrTrialEligible,
  resolveDynamicSections,
  resolveMenuSections,
  resolvePinnedSections,
} from "@/components/main-menu-navigation";
import * as LucideIcons from "lucide-react";
import { Home, LogOut, ChevronRight, Headphones } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { PlatformSubscriptionPlan } from "@repo/types";

export type MainMenuVariant = "default" | "platform";

interface MainMenuProps {
  sections?: NavigationSection[];
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
  variant?: MainMenuVariant;
  userRole?: string;
  userName?: string;
  userStaffId?: string;
  onLogout?: () => void;
}

const variantStyles: Record<MainMenuVariant, { surface: string; section: string; item: string; active: string }> = {
  default: {
    surface:
      "bg-white border-brand-100/60 shadow-[0_32px_68px_-32px_rgba(118,95,70,0.35)]",
    section: "text-xs font-semibold uppercase tracking-[0.32em] text-brand-500/85",
    item:
      "text-sm text-brand-700 hover:bg-white/70 hover:text-brand-800 hover:shadow-[0_12px_32px_-26px_rgba(118,95,70,0.3)]",
    active:
      "bg-white/85 text-brand-900 shadow-[0_18px_46px_-28px_rgba(118,95,70,0.38)] border border-brand-100/70"
  },
  platform: {
    surface:
      "bg-gradient-to-br from-[#f8f2ea]/95 via-[#f3ebdf]/75 to-[#ecdec9]/70 border-[#e2d4c1]/60 shadow-[0_32px_68px_-32px_rgba(118,95,70,0.35)]",
    section: "text-xs font-semibold uppercase tracking-[0.32em] text-[#a68960]/85",
    item:
      "text-sm text-[#6f5b3d] hover:bg-white/65 hover:text-[#4f3d28] hover:shadow-[0_12px_32px_-26px_rgba(118,95,70,0.28)]",
    active:
      "bg-white/82 text-[#3f301f] shadow-[0_18px_44px_-28px_rgba(118,95,70,0.35)] border border-[#d9c7ae]/80"
  }
};

const getInitialExpanded = (items: NavigationItem[], pathname: string) => {
  const expanded = new Set<string>();
  const traverse = (nodes: NavigationItem[], parents: string[]) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        const activeChild = containsActive(node.children, pathname);
        if (activeChild) {
          parents.concat(node.id).forEach((id) => expanded.add(id));
        }
        traverse(node.children, parents.concat(node.id));
      }
    });
  };
  traverse(items, []);
  return expanded;
};

const containsActive = (items: NavigationItem[], pathname: string): boolean =>
  items.some((item) => item.href === pathname || (item.children ? containsActive(item.children, pathname) : false));

const MenuList: React.FC<{
  items: NavigationItem[];
  pathname: string;
  isCollapsed: boolean;
  variant: MainMenuVariant;
  onCloseMobile: () => void;
  isMobileOpen: boolean;
}> = ({ items, pathname, isCollapsed, variant, onCloseMobile, isMobileOpen }) => {
  const [expanded, setExpanded] = React.useState(() => getInitialExpanded(items, pathname));

  const toggleNode = React.useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const styles = variantStyles[variant];

  return (
    <ul className="space-y-1">
      {items.map((item) => {
        const hasChildren = Boolean(item.children && item.children.length > 0);
        const isActive = item.href === pathname || (hasChildren && containsActive(item.children!, pathname));
        const isExpanded = expanded.has(item.id) || !hasChildren;
        const displayLabel = item.label;
        const iconName = typeof item.icon === "string" ? item.icon.trim() : "";
        const iconComponentName = resolveIconComponentName(iconName);
        const iconLibrary = LucideIcons as unknown as Record<string, LucideIcon>;
        const IconComponent = iconComponentName ? iconLibrary[iconComponentName] : undefined;
        const iconTextFallback =
          iconName && !IconComponent
            ? iconName.length <= 3
              ? iconName.toUpperCase()
              : iconName.charAt(0).toUpperCase()
            : displayLabel.charAt(0);
        return (
          <li key={item.id}>
            <div className="flex flex-col gap-1">
              <Link
                to={item.href}
                data-active={isActive}
                aria-current={item.href === pathname ? "page" : undefined}
                aria-label={isCollapsed ? displayLabel : undefined}
                className={cn(
                  "group flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-200",
                  styles.item,
                  isActive && styles.active,
                  isCollapsed && "justify-center px-0"
                )}
                title={isCollapsed ? displayLabel : undefined}
                onClick={() => {
                  if (isMobileOpen) {
                    onCloseMobile();
                  }
                }}
              >
                <span
                  aria-hidden
                  className={cn(
                    "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/70 text-sm font-semibold text-brand-600 shadow-sm",
                    isCollapsed ? "mx-0" : "-ml-1"
                  )}
                >
                  {IconComponent ? (
                    <IconComponent className="h-5 w-5" strokeWidth={1.8} aria-hidden />
                  ) : (
                    iconTextFallback
                  )}
                </span>
                {!isCollapsed && (
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{displayLabel}</span>
                      {typeof item.badge === "string" || typeof item.badge === "number" ? (
                        <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-[11px] font-semibold text-brand-700">
                          {item.badge}
                        </span>
                      ) : item.badge ? (
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                            item.badge.tone === "success" && "bg-success-100/90 text-success-700",
                            item.badge.tone === "warning" && "bg-warning-100/80 text-warning-700",
                            item.badge.tone === "danger" && "bg-danger-100/85 text-danger-700",
                            (!item.badge.tone || item.badge.tone === "brand") &&
                              "bg-brand-100/80 text-brand-700"
                          )}
                        >
                          {item.badge.label}
                        </span>
                      ) : null}
                    </div>
                    {item.description ? (
                      <p className="mt-1 truncate text-xs text-brand-500">{item.description}</p>
                    ) : null}
                  </div>
                )}
              </Link>

              {hasChildren ? (
                <div className={cn("pl-4", isCollapsed && "hidden")}>
                  <button
                    type="button"
                    onClick={() => toggleNode(item.id)}
                    className="ml-3 inline-flex items-center gap-2 text-xs font-medium text-brand-500 transition hover:text-brand-700"
                  >
                    <span>{isExpanded ? "收合" : "展開"}選項</span>
                    <span aria-hidden>{isExpanded ? "−" : "+"}</span>
                  </button>
                  {isExpanded ? (
                    <MenuList
                      items={item.children!}
                      pathname={pathname}
                      isCollapsed={false}
                      variant={variant}
                      onCloseMobile={onCloseMobile}
                      isMobileOpen={isMobileOpen}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const MainMenu: React.FC<MainMenuProps> = ({
  sections = [],
  isCollapsed,
  onToggleCollapsed,
  isMobileOpen,
  onCloseMobile,
  variant = "default",
  userRole,
  userName,
  userStaffId,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const styles = variantStyles[variant];
  const collapsedForView = isCollapsed && !isMobileOpen;

  const {
    data: userModules,
    isLoading: modulesLoading,
    error: modulesError
  } = useQuery<NormalizedUserModule[], Error>({
    queryKey: ["user-modules"],
    queryFn: fetchUserModules,
    enabled: variant === "default",
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60
  });

  const {
    data: currentSubscription,
    isLoading: subscriptionLoading
  } = useQuery({
    queryKey: ["subscription-current"],
    queryFn: fetchCurrentSubscription,
    enabled: variant === "default",
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60
  });

  const {
    data: trialModuleCodes
  } = useQuery({
    queryKey: ["trial-modules"],
    queryFn: fetchTrialModules,
    enabled: variant === "default",
    staleTime: 1000 * 60 * 10
  });

  const {
    data: subscriptionPlans
  } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: fetchSubscriptionPlans,
    enabled: variant === "default",
    staleTime: 1000 * 60 * 5
  });


  React.useEffect(() => {
    if (modulesError) {
      console.error("[MainMenu] 無法載入用戶模組", modulesError);
    }
  }, [modulesError]);

  const enabledModuleCodes = React.useMemo<Set<string> | null>(() => {
    return buildEnabledModuleCodes(userModules);
  }, [userModules]);

  const isSubscriptionActive = React.useMemo(() => {
    return isSubscriptionCurrentlyActive(currentSubscription);
  }, [currentSubscription]);

  const currentPlanObj = React.useMemo(() => {
    if (!isSubscriptionActive || !currentSubscription || !subscriptionPlans) return null;
    const planCode = currentSubscription.plan_code ?? currentSubscription.current_plan_id;
    if (!planCode) return null;
    return subscriptionPlans.find((plan) => plan.plan_code === planCode) ?? null;
  }, [isSubscriptionActive, currentSubscription, subscriptionPlans]);

  const currentPlanModuleCodes = React.useMemo(() => {
    return currentPlanObj ? extractPlanModuleCodes(currentPlanObj) : null;
  }, [currentPlanObj]);

  const currentPlanName = currentSubscription?.plan_name ?? currentPlanObj?.name ?? null;

  const isSubscriptionExpiredOrTrial = React.useMemo(() => {
    return isSubscriptionExpiredOrTrialEligible(currentSubscription, isSubscriptionActive);
  }, [currentSubscription, isSubscriptionActive]);

  const moduleLookup = React.useMemo(() => {
    return buildModuleLookup(userModules);
  }, [userModules]);

  const isStaffUser = userRole === "staff";

  const dynamicSections = React.useMemo<NavigationSection[] | null>(() => {
    return resolveDynamicSections({
      variant,
      userModules,
      isStaffUser,
      isSubscriptionActive,
      isSubscriptionExpiredOrTrial,
      currentPlanModuleCodes,
      trialModuleCodes,
      moduleLookup,
      enabledModuleCodes,
    });
  }, [variant, userModules, isStaffUser, isSubscriptionActive, isSubscriptionExpiredOrTrial, currentPlanModuleCodes, trialModuleCodes, moduleLookup, enabledModuleCodes]);

  const pinnedSections = React.useMemo<NavigationSection[]>(() => {
    return resolvePinnedSections(sections, enabledModuleCodes);
  }, [sections, enabledModuleCodes]);

  const menuSections = React.useMemo<NavigationSection[]>(() => {
    return resolveMenuSections({
      sections,
      dynamicSections,
      pinnedSections,
      enabledModuleCodes,
    });
  }, [sections, dynamicSections, pinnedSections, enabledModuleCodes]);

  const showModulesLoadingState =
    variant === "default" && (modulesLoading || subscriptionLoading) && menuSections.length === 0;
  const showModulesError =
    variant === "default" && Boolean(modulesError) && (!dynamicSections || dynamicSections.length === 0);
  const showEmptyState = !showModulesLoadingState && menuSections.length === 0;

  const menuContent = (
    <div className="relative h-full">
      <nav
        aria-label="主選單"
        className={cn(
          "flex h-full flex-col overflow-hidden rounded-[2rem] border px-3 py-6 transition-[width] duration-200",
          styles.surface,
          collapsedForView ? "w-[5.1rem]" : "w-[16.5rem]"
        )}
      >
        <div className={cn("flex items-center gap-3 px-3", collapsedForView && "justify-center px-0")}>
          <img src="/images/logo-icon.png" alt="Logo" className="h-11 w-11 rounded-2xl shadow object-cover" />
          {!collapsedForView ? (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-brand-800">mann.reservation</p>
              <p className="text-xs text-brand-500">柔性感知營運中心</p>
            </div>
          ) : null}
          {!collapsedForView && onLogout ? (
            <button aria-label="登出"
              type="button"
              onClick={onLogout}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl text-brand-400 transition hover:bg-white/80 hover:text-brand-700"
              title="登出"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.8} />
            </button>
          ) : null}
        </div>

        <div className="mt-6 flex-1 overflow-y-auto pr-1">
          <div className="space-y-6">
            {showModulesError ? (
              <div className="rounded-2xl border border-danger-100/70 bg-danger-50/70 px-4 py-3 text-xs text-danger-600 shadow-[0_12px_32px_-28px_rgba(203,130,110,0.24)]">
                模組資料載入失敗，已顯示預設選單。
              </div>
          ) : null}

          {showModulesLoadingState ? (
            <div className="space-y-3 px-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={`menu-skeleton-${index}`}
                  className="animate-pulse rounded-2xl border border-white/55 bg-white/70 p-4 shadow-[0_18px_48px_-34px_rgba(203,130,110,0.28)]"
                >
                  <div className="h-3 w-24 rounded-full bg-white/80" />
                  <div className="mt-3 space-y-2">
                    <div className="h-9 rounded-2xl bg-white/85" />
                    <div className="h-9 rounded-2xl bg-white/75" />
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {showEmptyState ? (
            <div className="rounded-2xl border border-brand-100/70 bg-white/75 px-4 py-4 text-xs text-brand-500 shadow-[0_18px_46px_-32px_rgba(118,95,70,0.24)]">
              尚未啟用任何模組。
            </div>
          ) : null}

          {menuSections.map((section) => (
            <div key={section.id}>
              {!isCollapsed ? <p className={cn("px-4", styles.section)}>{section.label}</p> : null}
              <div className={cn(!collapsedForView ? "mt-3" : "mt-0")}>
                <MenuList
                  items={section.items}
                  pathname={pathname}
                  isCollapsed={collapsedForView}
                  variant={variant}
                  onCloseMobile={onCloseMobile}
                  isMobileOpen={isMobileOpen}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plan badge */}
      {currentPlanName && !isStaffUser && (
        <div className={cn("mt-3 px-2", collapsedForView && "px-1")}>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            aria-label={collapsedForView ? `目前方案：${currentPlanName}` : undefined}
            className={cn(
              "w-full rounded-xl bg-brand-50/80 transition hover:bg-brand-100/80",
              collapsedForView ? "flex items-center justify-center py-2" : "flex items-center justify-between px-3 py-2"
            )}
          >
            {collapsedForView ? (
              <span className="text-[10px] font-semibold text-brand-600" title={currentPlanName}>P</span>
            ) : (
              <>
                <div className="text-left">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-brand-500">目前方案</div>
                  <div className="text-xs font-semibold text-brand-700">{currentPlanName}</div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-brand-400" strokeWidth={2} />
              </>
            )}
          </button>
        </div>
      )}

      {userName ? (
        <div className={cn("mt-2 px-2", collapsedForView && "px-1")}>
          <div className="rounded-2xl bg-white/70 p-2 space-y-1.5">
            {collapsedForView ? (
              <>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => userStaffId && navigate(`/staffs/${userStaffId}`)}
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-xl bg-brand-100/80 text-sm font-semibold text-brand-700 transition",
                      userStaffId && "cursor-pointer hover:bg-brand-200/80"
                    )}
                    title={userStaffId ? `${userName} — 編輯個人資料` : userName}
                    aria-label={userStaffId ? `${userName} — 編輯個人資料` : userName}
                  >
                    {userName.charAt(0)}
                  </button>
                </div>
                <div className="flex flex-col items-center gap-1">
                  {variant !== "platform" && (
                    <>
                      <button aria-label="首頁"
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        className="flex h-9 w-9 items-center justify-center rounded-xl text-brand-600 transition hover:bg-brand-50 hover:text-brand-800"
                        title="首頁"
                      >
                        <Home className="h-4.5 w-4.5" strokeWidth={1.8} />
                      </button>
                      <button aria-label="客服聯絡"
                        type="button"
                        onClick={() => navigate("/support")}
                        className="flex h-9 w-9 items-center justify-center rounded-xl text-brand-600 transition hover:bg-brand-50 hover:text-brand-800"
                        title="客服聯絡"
                      >
                        <Headphones className="h-4.5 w-4.5" strokeWidth={1.8} />
                      </button>
                    </>
                  )}
                  {onLogout ? (
                    <button aria-label="登出"
                      type="button"
                      onClick={onLogout}
                      className="flex h-9 w-9 items-center justify-center rounded-xl text-brand-600 transition hover:bg-brand-50 hover:text-brand-800"
                      title="登出"
                    >
                      <LogOut className="h-4.5 w-4.5" strokeWidth={1.8} />
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => userStaffId && navigate(`/staffs/${userStaffId}`)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left transition",
                    userStaffId && "cursor-pointer hover:bg-brand-50/80"
                  )}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-brand-100/80 text-xs font-semibold text-brand-700">
                    {userName.charAt(0)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-brand-800">{userName}</span>
                  {userStaffId ? <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-brand-400" strokeWidth={2} /> : null}
                </button>
                {variant !== "platform" && (
                  <>
                    <div className="flex flex-wrap justify-center gap-1.5 px-1">
                      <button
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-brand-50/80 px-3 py-1.5 text-xs font-medium text-brand-700 transition hover:bg-brand-100/80 hover:text-brand-800"
                      >
                        <Home className="h-3.5 w-3.5" strokeWidth={1.8} />
                        首頁
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate("/support")}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-brand-50/80 px-3 py-1.5 text-xs font-medium text-brand-700 transition hover:bg-brand-100/80 hover:text-brand-800"
                      >
                        <Headphones className="h-3.5 w-3.5" strokeWidth={1.8} />
                        客服
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className={cn("mt-2 px-2", collapsedForView && "px-1")}>
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-2xl bg-white/70 py-2 text-xs font-semibold text-brand-600 transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60",
            collapsedForView && "px-2 py-2"
          )}
        >
          <span aria-hidden>{collapsedForView ? "⤢" : "⤡"}</span>
          {!collapsedForView ? <span>收合選單</span> : null}
        </button>
      </div>
      </nav>

      <button
        type="button"
        onClick={onToggleCollapsed}
        className={cn(
          "fixed hidden h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-sm font-semibold text-brand-700 shadow-lg ring-1 ring-brand-100 transition hover:-translate-x-[60%] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/70",
          isMobileOpen ? "lg:hidden" : "lg:flex"
        )}
        style={{ top: "50%", left: collapsedForView ? "5.1rem" : "18rem" }}
        aria-label={collapsedForView ? "展開選單" : "收合選單"}
      >
        <span aria-hidden>{collapsedForView ? "⤢" : "⤡"}</span>
      </button>
    </div>
  );

  return (
    <>
      <div className="hidden h-full lg:flex">{menuContent}</div>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity lg:hidden",
          isMobileOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={onCloseMobile}
      />
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-full w-[80%] max-w-sm -translate-x-full transform p-4 transition-transform duration-300 ease-in-out lg:hidden",
          isMobileOpen && "translate-x-0"
        )}
      >
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between px-1 pb-4">
            <span className="text-sm font-semibold text-brand-700">主選單</span>
            <button
              type="button"
              onClick={onCloseMobile}
              className="rounded-full bg-white/80 px-3 py-1 text-sm font-medium text-brand-600 shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
            >
              關閉
            </button>
          </div>
          {React.cloneElement(menuContent as React.ReactElement, {
            className: cn(
              "flex h-full flex-col overflow-hidden rounded-[2rem] border px-3 py-6",
              styles.surface
            )
          })}
        </div>
      </div>
    </>
  );
};
